import React from "react";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Grid, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, Typography,
} from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { erpApi } from "../../../api/erp";
import { formatDateTime, formatMoney, entityId, PAYMENT_METHOD_LABELS } from "../../../utils/erp";
import { buildCashWorksheetCsv, collectCashWorksheet, CASH_WORKSHEET_KIND_LABELS, CASH_REVIEW_LABELS } from "../../../utils/cashWorksheet";
import WebProfitSpinner from "../../system/WebProfitSpinner";
import CashSalesSummary from "./CashSalesSummary";

// [WP-STABLE][CASH-WORKSHEET] La planilla es una proyección de movimientos ya auditados.
// No permite modificar importes originales ni sustituye la contabilidad ERP.
function exportWorksheet(data, options) {
  const csv = buildCashWorksheetCsv(data, options);
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `planilla-caja-${data.cashSession?.sessionNumber || "turno"}-${options.complete ? "completa" : `pagina-${options.page}`}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function SummaryCell({ title, value, currency, muted = false }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: "100%" }}>
      <Typography variant="caption" color="text.secondary">{title}</Typography>
      <Typography variant="h6" sx={{ fontWeight: 900, opacity: muted ? .6 : 1 }}>{value === null || value === undefined ? "Oculto" : formatMoney(value, currency)}</Typography>
    </Paper>
  );
}

export default function CashWorksheetDialog({ projectId, cashSessionId, onClose }) {
  const [result, setResult] = React.useState(null);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(50);
  const [loading, setLoading] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [error, setError] = React.useState("");
  const requestSequence = React.useRef(0);
  const exportSequence = React.useRef(0);
  const data = result?.projectId === projectId && result?.cashSessionId === cashSessionId
    && result?.page === page && result?.pageSize === pageSize ? result.data : null;

  React.useEffect(() => {
    setPage(0);
    setResult(null);
    return () => { exportSequence.current += 1; };
  }, [projectId, cashSessionId]);
  const refresh = React.useCallback(async () => {
    if (!cashSessionId) return;
    const requestId = ++requestSequence.current;
    try {
      setLoading(true);
      setError("");
      const loaded = await erpApi.cashSessionWorksheet(projectId, cashSessionId, { page: page + 1, pageSize });
      if (requestId === requestSequence.current) setResult({ projectId, cashSessionId, page, pageSize, data: loaded });
    } catch (err) { if (requestId === requestSequence.current) setError(err.message || "No se pudo consultar la planilla"); }
    finally { if (requestId === requestSequence.current) setLoading(false); }
  }, [projectId, cashSessionId, page, pageSize]);
  React.useEffect(() => {
    refresh();
    return () => { requestSequence.current += 1; };
  }, [refresh]);

  const exportCompleteWorksheet = async () => {
    if (!cashSessionId) return;
    const exportId = ++exportSequence.current;
    try {
      setExporting(true);
      setError("");
      const complete = await collectCashWorksheet(
        (number, size) => erpApi.cashSessionWorksheet(projectId, cashSessionId, { page: number, pageSize: size }),
        () => exportId !== exportSequence.current,
      );
      if (complete) exportWorksheet(complete, { complete: true });
    } catch (err) { if (exportId === exportSequence.current) setError(err.message || "No se pudo exportar la planilla"); }
    finally { if (exportId === exportSequence.current) setExporting(false); }
  };

  const close = () => {
    exportSequence.current += 1;
    requestSequence.current += 1;
    setExporting(false);
    onClose();
  };

  const session = data?.cashSession;
  const register = data?.register;
  const totals = data?.totals || {};
  const rows = data?.cashMovements || [];
  const currency = session?.currency || register?.currency || "ARS";
  return (
    <Dialog open={Boolean(cashSessionId)} onClose={close} fullWidth maxWidth="lg" aria-labelledby="wp-cash-worksheet-title" PaperProps={{ sx: { m: { xs: 1, sm: 4 }, width: { xs: "calc(100% - 16px)", sm: "calc(100% - 64px)" } } }}>
      <DialogTitle id="wp-cash-worksheet-title" sx={{ fontWeight: 900 }}>Planilla de caja {session?.sessionNumber || ""}</DialogTitle>
      <Divider />
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {loading && !data ? <Stack direction="row" spacing={1} alignItems="center"><WebProfitSpinner size={20} /><Typography>Cargando planilla…</Typography></Stack> : null}
          {session ? <>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
              <Box>
                <Typography sx={{ fontWeight: 900 }}>{register?.name || "Caja"} {register?.branchName ? `· ${register.branchName}` : ""}</Typography>
                <Typography variant="body2" color="text.secondary">Responsable: {session.openedBy?.name || "Sin nombre"} · Apertura: {formatDateTime(session.openedAt)}</Typography>
                {session.closedAt ? <Typography variant="body2" color="text.secondary">Cierre: {formatDateTime(session.closedAt)} · {session.closedBy?.name || "Sin nombre"}</Typography> : null}
              </Box>
              <Chip color={session.status === "open" ? "success" : "default"} label={session.status === "open" ? "Turno abierto" : "Turno cerrado"} />
            </Stack>
            <Grid container spacing={1.5}>
              <Grid item xs={6} md={3}><SummaryCell title="Apertura declarada" value={session.openingAmount} currency={currency} /></Grid>
              <Grid item xs={6} md={3}><SummaryCell title="Ingresos" value={totals.hidden ? null : totals.incoming} currency={currency} /></Grid>
              <Grid item xs={6} md={3}><SummaryCell title="Egresos" value={totals.hidden ? null : totals.outgoing} currency={currency} /></Grid>
              <Grid item xs={6} md={3}><SummaryCell title="Esperado" value={totals.hidden ? null : totals.expected} currency={currency} muted={totals.hidden} /></Grid>
            </Grid>
            <CashSalesSummary summary={data.salesSummary} currency={currency} />
            {session.status === "closed" ? <Alert severity={session.difference == null ? "info" : Number(session.difference) === 0 ? "success" : "warning"}>
              Declarado: <strong>{session.declaredClosingAmount == null ? "Sin información" : formatMoney(session.declaredClosingAmount, currency)}</strong> · Diferencia: <strong>{session.difference == null ? "Oculta" : formatMoney(session.difference, currency)}</strong> · {CASH_REVIEW_LABELS[session.reviewStatus] || "Sin revisión"}
              {session.reviewNotes ? ` · Revisión: ${session.reviewNotes}` : ""}
            </Alert> : null}
            {session.denominationCounts && Object.keys(session.denominationCounts).length ? <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={800}>Conteo de efectivo guardado en el cierre</Typography>
              <Typography variant="body2">{Object.entries(session.denominationCounts).filter(([, qty]) => Number(qty) > 0).map(([bill, qty]) => `${qty} × $${Number(bill).toLocaleString("es-AR")}`).join(" · ") || "Sin billetes"}{Number(session.otherCashAmount || 0) > 0 ? ` · Otros: ${formatMoney(session.otherCashAmount, currency)}` : ""}</Typography>
            </Paper> : null}
            {session.openingNotes || session.closingNotes ? <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              {session.openingNotes ? <Typography variant="body2">Apertura: {session.openingNotes}</Typography> : null}
              {session.closingNotes ? <Typography variant="body2">Cierre: {session.closingNotes}</Typography> : null}
            </Paper> : null}
            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>Movimientos del turno ({data.total || 0})</Typography>
            <Typography variant="caption" color="text.secondary">El saldo parte de la apertura. Las filas anuladas o de referencia histórica no lo modifican; apertura y cierre son registros de control.</Typography>
            {!totals.hidden && data.pageOpeningBalance != null ? <Typography variant="body2">Saldo anterior a esta página: <strong>{formatMoney(data.pageOpeningBalance, currency)}</strong></Typography> : null}
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 390 }}>
              <Table size="small" stickyHeader sx={{ minWidth: 860 }}>
                <TableHead><TableRow><TableCell>Fecha</TableCell><TableCell>Comprobante</TableCell><TableCell>Concepto y medio</TableCell><TableCell>Tipo</TableCell><TableCell align="right">Ingreso</TableCell><TableCell align="right">Egreso</TableCell>{!totals.hidden ? <TableCell align="right">Saldo</TableCell> : null}</TableRow></TableHead>
                <TableBody>
                  {rows.map((row) => <TableRow key={entityId(row)} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(row.occurredAt)}</TableCell>
                    <TableCell>{row.cashMovementNumber}{entityId(row.erpMovementId || row.movementId) ? <Typography variant="caption" sx={{ display: "block" }} color="text.secondary">ERP: {entityId(row.erpMovementId || row.movementId)}</Typography> : null}</TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
                      <Typography variant="body2">{row.description || "—"}</Typography>
                      <Typography variant="caption" color="text.secondary">{PAYMENT_METHOD_LABELS[row.method] || row.method}{row.createdByName ? ` · ${row.createdByName}` : ""}</Typography>
                      {row.status === "voided" || row.metadata?.operationalScope === "historical_reference" ? <Chip size="small" label={row.status === "voided" ? "Anulado · no suma" : "Histórico · no suma"} sx={{ display: "flex", width: "fit-content", mt: .5 }} /> : null}
                    </TableCell>
                    <TableCell>{CASH_WORKSHEET_KIND_LABELS[row.kind] || row.kind}</TableCell>
                    <TableCell align="right" sx={{ color: "success.main", fontWeight: 700 }}>{row.direction === "in" ? formatMoney(row.amount, currency) : "—"}</TableCell>
                    <TableCell align="right" sx={{ color: "error.main", fontWeight: 700 }}>{row.direction === "out" ? formatMoney(row.amount, currency) : "—"}</TableCell>
                    {!totals.hidden ? <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>{row.runningBalance == null ? "—" : formatMoney(row.runningBalance, currency)}</TableCell> : null}
                  </TableRow>)}
                  {!rows.length ? <TableRow><TableCell colSpan={totals.hidden ? 6 : 7}>Todavía no hay movimientos en este turno.</TableCell></TableRow> : null}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={data.total || 0} page={page} onPageChange={(_event, p) => setPage(p)} rowsPerPage={pageSize} rowsPerPageOptions={[25, 50, 100, 200]} onRowsPerPageChange={(event) => { setPageSize(Number(event.target.value)); setPage(0); }} labelRowsPerPage="Filas" SelectProps={{ disabled: exporting }} backIconButtonProps={{ disabled: exporting || page === 0 }} nextIconButtonProps={{ disabled: exporting || !data.hasMore }} sx={{ "& .MuiTablePagination-toolbar": { flexWrap: "wrap", pl: 0 } }} />
            {session.reviewHistory?.length ? <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={800}>Historial de revisiones</Typography>
              {session.reviewHistory.map((review, index) => <Typography key={entityId(review) || index} variant="body2" sx={{ mt: .5 }}>{formatDateTime(review.at)} · {review.by?.name || "Sin nombre"} · {CASH_REVIEW_LABELS[review.status] || review.status}{review.notes ? ` · ${review.notes}` : ""}</Typography>)}
            </Paper> : null}
            <Typography variant="caption" color="text.secondary">La planilla respeta permisos, responsables y cierre ciego. La exportación completa reúne todas las páginas del turno (hasta 20.000 movimientos), sin modificar asientos ni movimientos.</Typography>
          </> : null}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 2, py: 1.5, flexWrap: "wrap", gap: .5 }}>
        <Button startIcon={<RefreshRoundedIcon />} disabled={loading || exporting || !cashSessionId} onClick={refresh}>Actualizar</Button>
        <Button startIcon={<DownloadRoundedIcon />} disabled={!rows.length || loading || exporting} onClick={() => exportWorksheet(data, { complete: false, page: page + 1 })}>Página actual</Button>
        <Button startIcon={<DownloadRoundedIcon />} variant="outlined" disabled={!rows.length || loading || exporting} onClick={exportCompleteWorksheet}>{exporting ? "Preparando CSV…" : "CSV completo"}</Button>
        <Button variant="contained" onClick={close}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
